import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Switch, message, Form, Input, Upload } from "antd";
import { Helmet } from "react-helmet-async";
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import Header from "../../../components/Header";
import Sidebar from "../../../components/Sidebar";
import Breadcrumb from '../../../components/Common/Breadcrumb';
import LoadingSpinner from "../../../components/Common/LoadingSpinner";
import { onShowSizeChange, itemRender } from "../../../components/Pagination";
import { 
  createPopup, 
  updatePopup, 
  fetchAllPopup, 
  fetchPopupById,
  deletePopup 
} from "../../../services/popupService";
import { BASE_URL_IMAGE } from '../../../config/api';
import "./Popup.css";

const { TextArea } = Input;

const Popup = () => {
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedPopup, setSelectedPopup] = useState(null);
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [editImageFile, setEditImageFile] = useState(null);

  const breadcrumbItems = [
    { label: "Notification Management" },
    { label: "Popup" }
  ];

  
  // Fetch popups from backend
useEffect(() => {
  const fetchPopups = async () => {
    setLoading(true);
    try {
      const data = await fetchAllPopup();
      // Transform backend data to match frontend structure
      const transformedData = data.map(item => ({
        id: item.popup_id,
        title: item.popup_title,
        image: item.popup_image ? `${BASE_URL_IMAGE}/uploads/popupImages/${item.popup_image}` : "https://placehold.co/600x400/4f46e5/ffffff?text=No+Image",
        status: item.popup_status,
        redirect_url: item.popup_link,
        button_text: item.popup_button_text || "APPLY NOW", // Changed from hardcoded to dynamic
        additional_text: item.popup_content
      }));
      setPopups(transformedData);
    } catch (error) {
      console.error("Error fetching popups:", error);
      message.error(error.message || "Failed to fetch popups");
    } finally {
      setLoading(false);
    }
  };
  fetchPopups();
}, []);

 
  // Handle status toggle
const handleStatusChange = async (popupId, currentStatus) => {
  try {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    const popup = popups.find(p => p.id === popupId);
    
    const formData = new FormData();
    formData.append('title', popup.title);
    formData.append('link', popup.redirect_url || '');
    formData.append('btn_text', popup.button_text || 'APPLY NOW'); // Add this line
    formData.append('content', popup.additional_text || '');
    formData.append('status', newStatus);

    await updatePopup(popupId, formData);

    setPopups(prevPopups =>
      prevPopups.map(popup =>
        popup.id === popupId
          ? { ...popup, status: newStatus }
          : popup
      )
    );
    message.success(`Popup ${newStatus === "active" ? "activated" : "deactivated"} successfully`);
  } catch (error) {
    console.error(error);
    message.error(error.message || "Failed to update status");
  }
};

  // Handle view popup
  const handleViewPopup = (popup) => {
    setSelectedPopup(popup);
    setIsViewModalVisible(true);
  };

  // Handle edit popup
  const handleEditPopup = (popup) => {
    setSelectedPopup(popup);
    form.setFieldsValue({
      title: popup.title,
      redirect_url: popup.redirect_url,
      button_text: popup.button_text || "APPLY NOW",
      additional_text: popup.additional_text
    });
    setIsEditModalVisible(true);
  };

  // Handle add new popup
  const handleAddPopup = () => {
    setImageFile(null);
    form.resetFields();
    setIsAddModalVisible(true);
  };

  // Handle image upload for add
  const handleImageUpload = (file) => {
    setImageFile(file);
    return false; // Prevent default upload behavior
  };

  // Handle image upload for edit
  const handleEditImageUpload = (file) => {
    setEditImageFile(file);
    return false;
  };

  
  // Handle form submission for add
const handleAddSubmit = async (values) => {
  if (!imageFile) {
    message.error("Please upload a popup image");
    return;
  }

  setSubmitLoading(true);
  try {
    const formData = new FormData();
    formData.append('title', values.title);
    formData.append('link', values.redirect_url);
    formData.append('btn_text', values.button_text); // Add this line
    formData.append('content', values.additional_text || '');
    formData.append('status', 'active');
    formData.append('popup_image', imageFile);

    await createPopup(formData);

    // Refresh the popups list
    const data = await fetchAllPopup();
    const transformedData = data.map(item => ({
      id: item.popup_id,
      title: item.popup_title,
      image: item.popup_image ? `${BASE_URL_IMAGE}/uploads/popupImages/${item.popup_image}` : "https://placehold.co/600x400/4f46e5/ffffff?text=No+Image",
      status: item.popup_status,
      redirect_url: item.popup_link,
      button_text: item.popup_button_text || "APPLY NOW", // Update this line
      additional_text: item.popup_content
    }));
    setPopups(transformedData);
    
    message.success("Popup created successfully");
    setIsAddModalVisible(false);
    form.resetFields();
    setImageFile(null);
  } catch (error) {
    message.error(error.message || "Failed to create popup");
  } finally {
    setSubmitLoading(false);
  }
};

 
// Handle form submission for edit
const handleEditSubmit = async (values) => {
  setSubmitLoading(true);
  try {
    const formData = new FormData();
    formData.append('title', values.title);
    formData.append('link', values.redirect_url);
    formData.append('btn_text', values.button_text); // Add this line
    formData.append('content', values.additional_text || '');
    formData.append('status', selectedPopup.status);

    if (editImageFile) {
      formData.append('popup_image', editImageFile);
    }

    await updatePopup(selectedPopup.id, formData);

    // Refresh the popups list
    const data = await fetchAllPopup();
    const transformedData = data.map(item => ({
      id: item.popup_id,
      title: item.popup_title,
      image: item.popup_image ? `${BASE_URL_IMAGE}/uploads/popupImages/${item.popup_image}` : "https://placehold.co/600x400/4f46e5/ffffff?text=No+Image",
      status: item.popup_status,
      redirect_url: item.popup_link,
      button_text: item.popup_button_text || "APPLY NOW", // Update this line
      additional_text: item.popup_content
    }));
    setPopups(transformedData);
    
    message.success("Popup updated successfully");
    setIsEditModalVisible(false);
    setEditImageFile(null);
  } catch (error) {
    message.error(error.message || "Failed to update popup");
  } finally {
    setSubmitLoading(false);
  }
};
  // Handle delete popup
  const handleDeletePopup = async (popupId) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this popup?',
      content: 'This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      async onOk() {
        try {
          await deletePopup(popupId);
          setPopups(prevPopups => prevPopups.filter(popup => popup.id !== popupId));
          message.success('Popup deleted successfully');
        } catch (error) {
          message.error(error.message || 'Failed to delete popup');
        }
      }
    });
  };

  // Handle redirect from popup
  const handleRedirect = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  // Table Columns
  const columns = [
    {
      title: "Sr. No",
      key: "srno",
      render: (text, record, index) => index + 1,
      width: 80,
    },
   
    {
      title: "Popup Title",
      dataIndex: "title",
      key: "title",
      render: (title, record) => (
        <div className="d-flex align-items-center">
          <img
            src={record.image}
            alt={title}
            style={{
              width: 40,
              height: 40,
              objectFit: "cover",
              borderRadius: 4,
              marginRight: 12,
            }}
          />
          <span className="fw-semibold">{title}</span>
        </div>
      ),
    },
    {
    title: "Redirect URL",
    dataIndex: "redirect_url",
    key: "redirect_url",
    render: (url) => (
      <div>
        {url ? (
          <a 
            href={url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-primary"
            style={{ 
              wordBreak: 'break-all',
              fontSize: '12px'
            }}
          >
            {url.length > 50 ? `${url.substring(0, 50)}...` : url}
          </a>
        ) : (
          <span className="text-muted">No URL</span>
        )}
      </div>
    ),
    width: 200,
  },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <Switch
          checked={status === "active"}
          onChange={() => handleStatusChange(record.id, status)}
          size="small"
        />
      ),
      width: 100,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="action-buttons">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewPopup(record)}
            className="btn-view"
          >
            View
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditPopup(record)}
            className="btn-edit"
          >
            Edit
          </Button>
          <Button
            type="link"
            icon={<DeleteOutlined />}
            onClick={() => handleDeletePopup(record.id)}
            className="btn-delete"
            danger
          >
            Delete
          </Button>
        </div>
      ),
      width: 200,
    },
  ];

  return (
    <div>
      <Helmet><title>Popup | CRM Jnec</title></Helmet>
      <Header />
      <Sidebar activeClassName="notification/popup" />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <Breadcrumb items={breadcrumbItems} />
              </div>
            </div>
          </div>

          {loading ? (
            <LoadingSpinner message="Loading popups..." />
          ) : (
            <div className="card card-table show-entire">
              <div className="card-header">
                <h4 className="card-title">Popup Management</h4>
                <p className="card-text">Manage system popup notifications</p>
                <div className="card-actions">
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddPopup}
                    className="btn btn-primary"
                  >
                    Add New Popup
                  </Button>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <Table
                    columns={columns}
                    dataSource={popups}
                    rowKey="id"
                    pagination={{
                      total: popups.length,
                      showTotal: (total, range) =>
                        `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                      onShowSizeChange,
                      itemRender,
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Popup Preview Overlay */}
          {isViewModalVisible && (
            <div className="popup-preview-overlay">
              {/* Background with blur */}
              <div
                className="popup-background"
                style={{
                  backgroundImage: `url('https://jnec.org/upload/demo.png')`,
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  zIndex: 1,
                }}
              />

              {/* Blurred overlay */}
              <div className="blur-overlay" />

              {/* Popup Content */}
              <div className="popup-content">
                <div className="popup-card">
                  <div className="popup-header">
                    <div className="logo-container">
                      <img
                        src="https://placehold.co/100x80/623015/ffffff?text=JNEC"
                        alt="JNEC Logo"
                        className="popup-logo"
                      />
                    </div>
                    <div className="close-button" onClick={() => setIsViewModalVisible(false)}>
                      ✕
                    </div>
                  </div>
                  <div className="popup-image">
                    <img
                      src={selectedPopup?.image}
                      alt={selectedPopup?.title}
                      style={{
                        width: '100%',
                        height: 200,
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                  <div className="popup-body">
                    <h3 className="popup-title">
                      {selectedPopup?.title}
                    </h3>
                    {/* <div className="popup-subtitle">
                      Minor Specialisation: AI in Chemical Engineering, Business Analytics
                    </div> */}
                    {/* <div className="popup-highlight">
                      FEW SEATS AVAILABLE 2025 - 26
                    </div> */}
                    {/* <div className="popup-contact">
                      Contact: +91 9890717844
                    </div> */}
                    
                    {/* Additional HTML Content */}
                    {selectedPopup?.additional_text && (
                      <div 
                        className="popup-additional-text"
                        dangerouslySetInnerHTML={{ __html: selectedPopup.additional_text }}
                      />
                    )}
                    
                    <Button
                      type="primary"
                      size="large"
                      className="popup-button"
                      onClick={() => handleRedirect(selectedPopup?.redirect_url)}
                    >
                      {selectedPopup?.button_text || 'APPLY NOW'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Add Popup Modal */}
          <Modal
            title="Add New Popup"
            open={isAddModalVisible}
            onCancel={() => !submitLoading && setIsAddModalVisible(false)}
            footer={null}
            width={600}
            maskClosable={!submitLoading}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleAddSubmit}
            >
              <div className="text-center mb-4">
                <img
                  src={imageFile ? URL.createObjectURL(imageFile) : "https://placehold.co/400x200/3b82f6/ffffff?text=Popup+Image"}
                  alt="Popup Preview"
                  style={{
                    width: '100%',
                    maxHeight: 200,
                    objectFit: 'cover',
                    borderRadius: 8,
                  }}
                />
                <Upload
                  beforeUpload={handleImageUpload}
                  accept="image/*"
                  showUploadList={false}
                  className="mt-2"
                >
                  <Button icon={<UploadOutlined />}>Upload Image</Button>
                </Upload>
                {!imageFile && (
                  <div className="text-danger mt-1">
                    * Image is required
                  </div>
                )}
              </div>

              <Form.Item
                name="title"
                label="Popup Title"
                rules={[{ required: true, message: 'Please enter popup title' }]}
              >
                <Input placeholder="Enter popup title" />
              </Form.Item>

              <Form.Item
                name="redirect_url"
                label="Redirect URL"
                rules={[{ required: true, message: 'Please enter redirect URL' }]}
              >
                <Input placeholder="Enter redirect URL" />
              </Form.Item>

              <Form.Item
                name="button_text"
                label="Button Text"
                initialValue="APPLY NOW"
                rules={[{ required: true, message: 'Please enter button text' }]}
              >
                <Input placeholder="Enter button text" />
              </Form.Item>

              <Form.Item
                name="additional_text"
                label="Additional Text (HTML Format)"
                rules={[{ required: false }]}
              >
                <TextArea 
                  rows={4} 
                  placeholder="Enter additional text in HTML format (e.g., <p>Text</p><ul><li>Item 1</li></ul>)" 
                />
              </Form.Item>

              <Form.Item className="text-right mb-0">
                <Button 
                  onClick={() => setIsAddModalVisible(false)} 
                  style={{ marginRight: 8 }}
                  disabled={submitLoading}
                >
                  Cancel
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  loading={submitLoading}
                  disabled={!imageFile}
                >
                  Create Popup
                </Button>
              </Form.Item>
            </Form>
          </Modal>

          {/* Edit Popup Modal */}
          <Modal
            title="Edit Popup"
            open={isEditModalVisible}
            onCancel={() => !submitLoading && setIsEditModalVisible(false)}
            footer={null}
            width={600}
            maskClosable={!submitLoading}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleEditSubmit}
            >
              <div className="text-center mb-4">
                <img
                  src={editImageFile ? URL.createObjectURL(editImageFile) : selectedPopup?.image}
                  alt={selectedPopup?.title}
                  style={{
                    width: '100%',
                    maxHeight: 200,
                    objectFit: 'cover',
                    borderRadius: 8,
                  }}
                />
                <Upload
                  beforeUpload={handleEditImageUpload}
                  accept="image/*"
                  showUploadList={false}
                  className="mt-2"
                >
                  <Button icon={<UploadOutlined />}>Change Image</Button>
                </Upload>
              </div>

              <Form.Item
                name="title"
                label="Popup Title"
                rules={[{ required: true, message: 'Please enter popup title' }]}
              >
                <Input placeholder="Enter popup title" />
              </Form.Item>

              <Form.Item
                name="redirect_url"
                label="Redirect URL"
                rules={[{ required: true, message: 'Please enter redirect URL' }]}
              >
                <Input placeholder="Enter redirect URL" />
              </Form.Item>

              <Form.Item
                name="button_text"
                label="Button Text"
                rules={[{ required: true, message: 'Please enter button text' }]}
              >
                <Input placeholder="Enter button text" />
              </Form.Item>

              <Form.Item
                name="additional_text"
                label="Additional Text (HTML Format)"
                rules={[{ required: false }]}
              >
                <TextArea 
                  rows={4} 
                  placeholder="Enter additional text in HTML format (e.g., <p>Text</p><ul><li>Item 1</li></ul>)" 
                />
              </Form.Item>

              <Form.Item className="text-right mb-0">
                <Button 
                  onClick={() => setIsEditModalVisible(false)} 
                  style={{ marginRight: 8 }}
                  disabled={submitLoading}
                >
                  Cancel
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  loading={submitLoading}
                >
                  Update Popup
                </Button>
              </Form.Item>
            </Form>
          </Modal>
        </div>
      </div>
    </div>
  );
}

export default Popup;